import { isEmpty, cloneDeep } from 'common/utils/vanilla'
import { ContractSelection } from './ContractSelection/ContractSelection'
import {
  getPaymentOptionDisplayValue,
  PaymentType,
} from './PaymentType/PaymentType'
import {
  AzureOfferPlan,
  AzurePricingType,
  Installments,
  RecurrentPriceModeType,
} from '../../../../modules/privateOffer/reducer'
import { Product } from '../../../../modules/productsListing/reducer'
import { useMemo } from 'react'
import {
  isNonEmptyNumber,
  calculateDiscountPercentage,
} from '../../../../../common/utils/getDiscountedPriceValue'
import { FormikProps, useFormikContext } from 'formik'
import { MicrosoftInstallments } from '../../MicrosoftInstallments/MicrosoftInstallments'
import { Divider } from '@mui/material'
import { MeteringDimensions } from './MeteringDimensions/MeteringDimensions'
import { useStyles } from './PlanRow.styles'
import { MinMax } from './MinMax/MinMax'

export const PlanRowComponent = ({
  index,
  plan,
  product,
}: {
  index: number
  plan: AzureOfferPlan
  product: Product
}) => {
  const { values, setFieldValue } = useFormikContext() as FormikProps<
    Record<string, unknown>
  >
  const { classes } = useStyles()

  const mppoDuration = getMppoDuration({ plan })

  const paymentTermItem = useMemo(() => {
    return {
      ...cloneDeep(plan.pricing.recurrentPrice.prices[0].paymentOption),
    }
  }, [plan])

  const billingTerm = plan?.pricing.recurrentPrice?.prices[0]?.billingTerm
  const paymentOption = plan?.pricing?.recurrentPrice?.prices[0]?.paymentOption

  const shouldShowUserMinMax =
    plan?.pricing?.recurrentPrice?.recurrentPriceMode ===
    RecurrentPriceModeType.PERUSER

  const shouldShowMetering =
    plan?.pricing?.recurrentPrice?.recurrentPriceMode ===
      RecurrentPriceModeType.FLATRATE &&
    plan?.pricing?.customMeters &&
    Object.keys(plan.pricing?.customMeters?.meters || {}).length > 0

  const paymentOptionValue = getPaymentOptionDisplayValue(
    billingTerm,
    paymentOption
  )

  const selectedPaymentOptionValue = useMemo(() => {
    if (
      plan.pricing.recurrentPrice.prices[0]?.installments &&
      plan.pricing.recurrentPrice.prices[0]?.installments?.schedule?.length
    ) {
      return 'flexiblePayment'
    }

    if (isNonEmptyNumber(paymentTermItem?.value)) {
      return paymentOptionValue
    }

    return ''
  }, [paymentTermItem, paymentOptionValue, plan, values])

  const handleChangeInstallmentsAmount = (installments: Installments[]) => {
    const rawTotal = installments.reduce((sum, installment) => {
      const amount = installment.pricePerPaymentInUsd || 0
      return sum + (typeof amount === 'number' ? amount : 0)
    }, 0)

    const total = parseFloat(rawTotal.toFixed(5))

    const azurePricingType = values?.azurePricingType as AzurePricingType
    const isDiscountPricing =
      azurePricingType === AzurePricingType.LABRA_PERCENTAGE

    if (isDiscountPricing) {
      const originalPricingPrices =
        plan?.originalPricing?.recurrentPrice?.prices?.filter(
          item =>
            item?.billingTerm?.type ===
              plan.pricing.recurrentPrice.prices[0].billingTerm.type &&
            item?.billingTerm?.value ===
              plan.pricing.recurrentPrice.prices[0].billingTerm.value
        ) || []
      if (originalPricingPrices.length > 0) {
        const originalPrice = originalPricingPrices[0].pricePerPaymentInUsd || 0

        if (isNonEmptyNumber(originalPrice) && originalPrice > 0) {
          const calculatedDiscountPercentage = calculateDiscountPercentage(
            originalPrice,
            total
          )

          setFieldValue(
            `plans.${index}.discountPercentage`,
            calculatedDiscountPercentage
          )
        }
      }
    }

    setFieldValue(
      `plans.${index}.pricing.recurrentPrice.prices[0].installments.tcv`,
      total
    )
  }

  return (
    <>
      <ContractSelection
        index={index}
        plan={plan}
        product={product}
        key={plan.labraPlanId}
        selectedPaymentOptionValue={selectedPaymentOptionValue}
      />
      <PaymentType
        plan={plan}
        index={index}
        durationInMonths={mppoDuration}
        selectedPaymentOptionValue={selectedPaymentOptionValue}
      />
      {!isEmpty(plan.pricing.recurrentPrice.prices[0]?.installments) &&
        mppoDuration > 1 && (
          <>
            <Divider />
            <MicrosoftInstallments
              plan={plan}
              installments={
                plan.pricing.recurrentPrice.prices[0]?.installments?.schedule
              }
              index={index}
              durationInMonths={mppoDuration}
              offerStartDate={(values?.offerStartDate as string) || ''}
              tcv={plan?.pricing.recurrentPrice.prices[0].installments?.tcv}
              handleChangeInstallmentsAmount={handleChangeInstallmentsAmount}
            />
          </>
        )}
      {(shouldShowUserMinMax || shouldShowMetering) && (
        <Divider className={classes.divider} />
      )}
      {shouldShowUserMinMax && <MinMax plan={plan} index={index} />}
      {shouldShowMetering && <MeteringDimensions plan={plan} index={index} />}
    </>
  )
}

export const getMppoDuration = ({ plan }: { plan: AzureOfferPlan }) => {
  if (
    !plan ||
    !plan?.pricing ||
    !plan?.pricing?.recurrentPrice ||
    !plan?.pricing?.recurrentPrice?.prices ||
    !plan?.pricing?.recurrentPrice?.prices[0] ||
    !plan?.pricing?.recurrentPrice?.prices[0]?.billingTerm
  ) {
    return 0
  }
  const billingTermItem = {
    ...cloneDeep(plan.pricing.recurrentPrice.prices[0].billingTerm),
  }
  return getDurationValueFromBillingTerm(billingTermItem)
}

export const getDurationValueFromBillingTerm = (billingTerm: {
  type: string
  value: number
}) => {
  if (billingTerm.type === 'month') {
    return billingTerm.value
  } else if (billingTerm.type === 'year') {
    return billingTerm.value * 12
  }

  return 0
}
